import { AlertPresenter } from '../../AlertPresenter';
import { EditNoteFormViewModel } from './EditNoteFormViewModel';
import { INoteFieldConflict, NoteField } from './NoteConflictDetector';
import { NoteBodyConflictPresenter } from './NoteBodyConflictPresenter';

const FIELD_LABELS: Record<NoteField, string> = { title: 'Title', html: 'Note body', isPrivate: 'Visibility', tags: 'Tags' };

export class NoteConflictResolver {
    static inject = () => [AlertPresenter, NoteBodyConflictPresenter];
    constructor(private alert: AlertPresenter, private bodyPresenter: NoteBodyConflictPresenter) {}

    private isResolving = false;

    resolveAll = async (vm: EditNoteFormViewModel) => {
        // re-entrancy guard: each resolveConflict() triggers setState, which re-runs the editor's effect
        if (this.isResolving) {
            return;
        }
        this.isResolving = true;
        try {
            let conflicts = vm.getPendingConflicts();
            while (conflicts.length > 0) {
                const conflict = conflicts[0];
                const choice = await this.promptForField(conflict);
                vm.resolveConflict(conflict.field, choice);
                conflicts = vm.getPendingConflicts();
            }
        } finally {
            this.isResolving = false;
        }
    };

    private promptForField = async (conflict: INoteFieldConflict): Promise<'mine' | 'theirs'> => {
        // the large rich-text body can't be shown usefully in an alert; open a read-only drawer
        // rendering THEIR version full-fidelity (mine is already in the editor).
        if (conflict.field === 'html') {
            return this.bodyPresenter.present({ theirsHtml: (conflict.theirs as string) || '' });
        }

        // scalar fields fit a lightweight inline alert with a short preview
        const label = FIELD_LABELS[conflict.field];
        const result = await this.alert.showAlertV2({
            header: `${label} was changed by someone else`,
            drawerAlert: true,
            wider: true,
            message: `Your version:\n${this.preview(conflict.field, conflict.mine)}\n\nTheir version:\n${this.preview(conflict.field, conflict.theirs)}`,
            options: [
                { label: 'Keep mine', id: 'mine' },
                { label: 'Use theirs', id: 'theirs' }
            ]
        });
        return result === 'theirs' ? 'theirs' : 'mine';
    };

    // scalar-field preview only (the html body uses the read-only drawer, not this)
    private preview = (field: NoteField, value: unknown): string => {
        if (field === 'isPrivate') {
            return value ? 'Private' : 'Shared';
        }
        if (field === 'tags') {
            const tags = (value as string[]) || [];
            return tags.length ? tags.join(', ') : '(none)';
        }
        const text = (value as string) || '(empty)';
        return text.length > 200 ? `${text.slice(0, 200)}…` : text;
    };
}
